import { styled } from '@linaria/react';
import { Group, Tooltip } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import type { ModelComparisonRow } from '../../../api/agent-miner-client/agent-miner-client.type';
import type { TableColumn } from '../../../components/table/table';
import { inkDark, inkDarkest, inkLight } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';
import { fmtMetric, fmtUsd, reworkTone, reworkToneColor } from './model-comparison.helpers';
import { TrendSparkline } from './trend-sparkline';

/**
 * Column header with an info icon whose tooltip explains how the metric is
 * computed.
 *
 * @param {string} label - The visible column label
 * @param {string} tooltip - The explanation shown on hover
 * @returns {ReactNode} The composed header content
 */
const headerWithInfo = (label: string, tooltip: string): ReactNode => (
  <Group gap={4} wrap="nowrap">
    {label}
    <Tooltip label={tooltip} position="top" withArrow multiline maw={280}>
      <Icon name="info" size={13} color={inkLight} style={{ cursor: 'help' }} />
    </Tooltip>
  </Group>
);

/**
 * Builds the Model Comparison table columns. Rows are per model family
 * (sessions from every agent tool merge). The rework column is colored with
 * INVERTED polarity (low = green); the cost column is omitted entirely when
 * the caller lacks cost visibility so hidden costs never render.
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @param {boolean} costVisible - meta.cost_visible from the payload
 * @returns {TableColumn<ModelComparisonRow>[]} Column definitions for `<Table>`
 */
const getModelComparisonColumns = (t: TFunction, costVisible: boolean): TableColumn<ModelComparisonRow>[] => {
  const columns: TableColumn<ModelComparisonRow>[] = [
    {
      key: 'model_family',
      label: t('agent_miner.model_comparison.model'),
      width: '200px',
      sortable: true,
      render: (_, row) => <FamilyLabel>{row.model_family}</FamilyLabel>,
    },
    {
      key: 'sessions',
      label: t('agent_miner.sessions'),
      width: '100px',
      sortable: true,
      render: (_, row) => <NumberCell>{row.sessions.toLocaleString()}</NumberCell>,
    },
    {
      key: 'rework_rate_pct',
      label: headerWithInfo(
        t('agent_miner.model_comparison.rework_rate'),
        t('agent_miner.model_comparison.rework_rate_tooltip'),
      ),
      width: '110px',
      sortable: true,
      sortValue: (row) => row.rework_rate_pct ?? Number.POSITIVE_INFINITY,
      render: (_, row) => (
        <NumberCell style={{ color: reworkToneColor(reworkTone(row.rework_rate_pct)), fontWeight: 600 }}>
          {fmtMetric(row.rework_rate_pct, '%')}
        </NumberCell>
      ),
    },
    {
      key: 'median_cycle_time_days',
      label: headerWithInfo(
        t('agent_miner.model_comparison.cycle_days'),
        t('agent_miner.model_comparison.cycle_days_tooltip'),
      ),
      width: '110px',
      sortable: true,
      sortValue: (row) => row.median_cycle_time_days ?? Number.POSITIVE_INFINITY,
      render: (_, row) => <NumberCell>{fmtMetric(row.median_cycle_time_days, 'd')}</NumberCell>,
    },
    {
      key: 'tasks_per_dev_per_week',
      label: headerWithInfo(
        t('agent_miner.model_comparison.tasks_per_dev_week'),
        t('agent_miner.model_comparison.tasks_per_dev_week_tooltip'),
      ),
      width: '130px',
      sortable: true,
      sortValue: (row) => row.tasks_per_dev_per_week ?? Number.NEGATIVE_INFINITY,
      render: (_, row) => <NumberCell>{fmtMetric(row.tasks_per_dev_per_week)}</NumberCell>,
    },
  ];

  if (costVisible) {
    columns.push({
      key: 'cost_per_completed_task_usd',
      label: headerWithInfo(
        t('agent_miner.model_comparison.cost_per_task'),
        t('agent_miner.model_comparison.cost_per_task_tooltip'),
      ),
      width: '120px',
      sortable: true,
      sortValue: (row) => row.cost_per_completed_task_usd ?? Number.POSITIVE_INFINITY,
      render: (_, row) => <NumberCell>{fmtUsd(row.cost_per_completed_task_usd)}</NumberCell>,
    });
  }

  columns.push({
    key: 'trend_completed_tasks',
    label: headerWithInfo(t('agent_miner.model_comparison.trend'), t('agent_miner.model_comparison.trend_tooltip')),
    width: '110px',
    sortable: false,
    render: (_, row) => <TrendSparkline data={row.trend_completed_tasks} />,
  });

  return columns;
};

const FamilyLabel = styled.span`
  color: ${inkDarkest};
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NumberCell = styled.span`
  color: ${inkDark};
  font-size: 13px;
  font-variant-numeric: tabular-nums;
`;

export { getModelComparisonColumns };
